
import axios from 'axios';
import { API_BASE_URL as BASE } from './apiConfig';
const API_BASE_URL = `${BASE}/api/crawler`;

// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: 토큰 + 팀 컨텍스트 헤더 자동 추가
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 팀 컨텍스트 전달 (BE-G1: X-Team-Id 헤더)
    const activeTeamId = sessionStorage.getItem('activeTeamId');
    if (activeTeamId && config.headers) {
      config.headers['X-Team-Id'] = activeTeamId;
    }
    // FormData 전송 시 Content-Type 제거 → axios/브라우저가 multipart/form-data; boundary=... 자동 설정
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 처리
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 토큰 만료 시 로그인 페이지로
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// 크롤러 API
export const crawlerApi = {
  // 웹사이트 스캔
  scan: async (url: string) => {
    const response = await apiClient.post('/scan', { url });
    return response.data;
  },

  // 이미지 목록 조회 (페이지네이션)
  getImages: async (params: {
    page: number;
    size: number;
    status?: string;
    url?: string;
    sourceType?: string;
    sortBy?: string;
    sortDir?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', params.page.toString());
    queryParams.append('size', params.size.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.url) queryParams.append('url', params.url);
    if (params.sourceType) queryParams.append('sourceType', params.sourceType);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);

    const response = await apiClient.get(`/images?${queryParams.toString()}`);
    return response.data;
  },

  // 웹사이트 목록
  getWebsites: async () => {
    const response = await apiClient.get('/websites');
    return response.data;
  },

  // ALT 텍스트 일괄 생성
  generateAlt: async () => {
    const response = await apiClient.post('/generate-alt');
    return response.data;
  },

  // 이미지 URL로 ALT 생성
  generateAltFromUrl: async (imageUrl: string) => {
      const response = await apiClient.post('/image-url', { imageUrl });
    return response.data;
  },

    // 이미지 URL로 ALT 생성 (imageBase64/컨텍스트 프로필 포함 전체 버전)
    generateAltFromUrlFull: async (
      imageUrl: string,
      imageBase64?: string | null,
      useContextProfile?: boolean,
    ) => {
      const response = await apiClient.post('/image-url', {
        imageUrl,
        ...(imageBase64 ? { imageBase64 } : {}),
        useContextProfile: useContextProfile ?? false,
      });
      return response.data;
    },

  // 단일 이미지 업로드
  uploadSingleImage: async (file: File) => {
    const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/image-upload', formData);
    return response.data;
  },

  // 다중 이미지 업로드
  uploadMultipleImages: async (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
        formData.append('files', file);
    });

      const response = await apiClient.post('/image-upload-multiple', formData);
    return response.data;
  },

    // 테스트 이미지 업로드 (POST /api/crawler/test-upload, multipart/form-data)
    testUpload: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/test-upload', formData);
      return response.data;
    },

  // ALT 재생성
  regenerateAlt: async (imageId: number) => {
    const response = await apiClient.post(`/images/${imageId}/regenerate`);
    return response.data;
  },

  // 이미지 전체 삭제
  clearImages: async (url?: string) => {
    const queryParam = url ? `?url=${encodeURIComponent(url)}` : '';
    const response = await apiClient.delete(`/images/clear${queryParam}`);
    return response.data;
  },

  // 개별 이미지 삭제
  deleteImage: async (imageId: number) => {
    const response = await apiClient.delete(`/images/${imageId}`);
    return response.data;
  },
};

export default apiClient;
